import { FC, useState } from 'react';

import { useMeetTools } from '../../../features/meet/MeetToolsProvider';
import { useAppDispatch } from '../../../hooks/redux';
import { clearEmotionsClientSelections } from '../../../store/emotionsGroup.slice';

import ModalWrap from '../../common/modal-wrap';
import Button from '../../common/button';
import { CloseCrossBtn } from '../../icons/close-cross-btn';

import { EmotionsGroupPsychologistResults } from './emotions-group-psychologist-results';

const EMOTIONS_CONFIRM_CLOSE = {
  name: '«Детектор эмоций»',
  description: 'Клиенты перестанут видеть выбор эмоций.',
};

export interface EmotionsGroupPanelProps {
  onClose: () => void;
  onConfirm?: () => void;
  sendEmotionsOpen?: () => void;
  sendEmotionsClose?: () => void;
  expectedParticipantIds: string[];
  participantDisplayNames?: Record<string, string>;
  /** Панель развёрнута на всю высоту поверх видео */
  isExpanded?: boolean;
  /** Переключить развёрнутое состояние */
  onExpandToggle?: () => void;
  /** Вызывается при переключении таба — для трансляции эмоций на «общий экран» клиентам */
  onActiveTabChange?: (participantLabel: string, emo: string[]) => void;
}

/**
 * Нижняя панель Детектора эмоций для психолога в групповой терапии.
 * Первый экран: кнопка «Запросить» → отправка emotions_open и переход к результатам.
 * Второй экран: счётчик ответивших и табы по участникам. Закрытие — модалка и sendEmotionsClose.
 */
export const EmotionsGroupPanel: FC<EmotionsGroupPanelProps> = ({
  onClose,
  onConfirm,
  sendEmotionsOpen,
  sendEmotionsClose,
  expectedParticipantIds,
  participantDisplayNames,
  isExpanded = false,
  onExpandToggle,
  onActiveTabChange,
}) => {
  const dispatch = useAppDispatch();
  const tools = useMeetTools();
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);

  const handleConfirm = () => {
    /**
     * Снимок участников храним в провайдере встречи, чтобы состояние
     * не сбрасывалось при временной перестройке layout (например, screen share).
     */
    tools.setEmotionsGroupExpectedParticipantIds([...expectedParticipantIds]);
    tools.setEmotionsGroupParticipantDisplayNames({ ...participantDisplayNames });
    dispatch(clearEmotionsClientSelections());
    sendEmotionsOpen?.();
    tools.setEmotionsGroupResultsShown(true);
    onConfirm?.();
  };

  const handleRequestClose = () => {
    setIsConfirmCloseOpen(true);
  };

  const handleConfirmClose = () => {
    setIsConfirmCloseOpen(false);
    sendEmotionsClose?.();
    onClose();
  };

  const handleCancelClose = () => {
    setIsConfirmCloseOpen(false);
  };

  const confirmMessage = `Вы действительно хотите закрыть инструмент ${EMOTIONS_CONFIRM_CLOSE.name}? ${EMOTIONS_CONFIRM_CLOSE.description}`;

  const confirmCloseModal = (
    <ModalWrap isShow={isConfirmCloseOpen}>
      <div className="think-scroll flex flex-col gap-6 w-full max-w-[90%] sm:max-w-[60%] md:max-w-[560px] h-auto rounded-[20px] p-6 lg:p-10 bg-white shadow-shadowPopup">
        <div className="w-full flex justify-between items-start">
          <h2 className="font-comfortaa text-lg lg:text-2xl text-[#111026] font-bold">Закрытие инструмента</h2>
          <button className="cursor-pointer" onClick={handleCancelClose} aria-label="Отменить закрытие">
            <CloseCrossBtn width={20} height={20} />
          </button>
        </div>
        <p className="text-sm md:text-base font-bold text-[#585858]">{confirmMessage}</p>
        <div className="flex justify-end gap-4">
          <Button type="grey" className="!w-max px-4 py-2 !text-sm md:!text-base" onClick={handleCancelClose}>
            Оставить открытым
          </Button>
          <Button type="black" className="!w-max px-4 py-2 !text-sm md:!text-base" onClick={handleConfirmClose}>
            Закрыть инструмент
          </Button>
        </div>
      </div>
    </ModalWrap>
  );

  if (tools.emotionsGroupResultsShown) {
    return (
      <>
        <EmotionsGroupPsychologistResults
          expectedParticipantIds={tools.emotionsGroupExpectedParticipantIds}
          participantDisplayNames={tools.emotionsGroupParticipantDisplayNames}
          onClose={handleRequestClose}
          isExpanded={isExpanded}
          onExpandToggle={onExpandToggle}
          onActiveTabChange={onActiveTabChange}
        />
        {confirmCloseModal}
      </>
    );
  }

  return (
    <>
      <div className="h-full w-full bg-white rounded-[5px] md:rounded-[15px] overflow-hidden flex flex-col p-4">
        <div className="font-manrope font-bold text-2xl text-[#2C3038] mb-4">Детектор эмоций</div>
        <div className="text-[#585858] text-base mb-6">
          Запросить у участников группы их текущее эмоциональное состояние. У каждого откроется выбор эмоций.
        </div>
        <div className="mt-auto flex flex-row">
          <Button
            type="primary"
            className="!text-base xl:!text-base !self-center"
            onClick={handleConfirm}
          >
            Запросить
          </Button>
        </div>
      </div>
      {confirmCloseModal}
    </>
  );
};
